import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import DashboardLayout from './Layout/DashboardLayout';
import './RoleManagement.css';

const ADMIN_PERMISSIONS = [
  { key: 'canManageUsers', label: 'Gerir Utilizadores', icon: '👥', desc: 'Criar, editar e eliminar utilizadores' },
  { key: 'canManageBookings', label: 'Gerir Reservas', icon: '📅', desc: 'Ver e gerir todas as reservas' },
  { key: 'canManageTimeSlots', label: 'Gerir Horários', icon: '🕐', desc: 'Criar e editar horários de aulas' },
  { key: 'canManageWorkoutPlans', label: 'Gerir Planos Treino', icon: '💪', desc: 'Criar e atribuir planos de treino' },
  { key: 'canViewReports', label: 'Ver Relatórios', icon: '📊', desc: 'Acesso a relatórios e exportações' },
  { key: 'canViewAnalytics', label: 'Ver Análises', icon: '📈', desc: 'Dashboard analytics avançado' },
  { key: 'canManageAI', label: 'Gerir IA', icon: '🤖', desc: 'Painel de administração da IA' },
  { key: 'canManageTokens', label: 'Gerir Tokens', icon: '🪙', desc: 'Atribuir e gerir tokens de utilizadores' },
  { key: 'canManageExerciseLibrary', label: 'Gerir Biblioteca de Exercícios', icon: '📚', desc: 'Criar e editar exercícios, equipamento e imagens da biblioteca' },
];

const FEATURE_PERMISSIONS = [
  { key: 'canAccessAITrainer', label: 'MMIA - AI Trainer', icon: '🤖', desc: 'Acesso ao Personal Trainer Virtual' },
  { key: 'canAccessBookings', label: 'Reservas', icon: '📅', desc: 'Reservar e ver reservas' },
  { key: 'canAccessClasses', label: 'Aulas', icon: '🎬', desc: 'Acesso a aulas online' },
  { key: 'canAccessWorkoutPlans', label: 'Plano de Treino', icon: '💪', desc: 'Ver e usar planos de treino' },
  { key: 'canAccessMealPlans', label: 'Plano Alimentar', icon: '🥗', desc: 'Ver e usar planos alimentares' },
  { key: 'canAccessGamification', label: 'Conquistas', icon: '🏆', desc: 'Sistema de gamificação e conquistas' },
  { key: 'canAccessTokenShop', label: 'Loja Tokens', icon: '🛒', desc: 'Acesso à loja de tokens' },
  { key: 'canAccessMessages', label: 'Mensagens', icon: '💬', desc: 'Enviar e receber mensagens' },
  { key: 'canAccessCredits', label: 'Créditos', icon: '💳', desc: 'Ver e gerir créditos' },
  { key: 'canAccessSmartwatch', label: 'Smartwatch', icon: '⌚', desc: 'Integração com smartwatch' },
  { key: 'canAccessNotifications', label: 'Notificações', icon: '🔔', desc: 'Receber notificações' },
];

const ALL_PERMISSIONS = [...ADMIN_PERMISSIONS, ...FEATURE_PERMISSIONS];

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    roleName: '',
    description: '',
    canManageUsers: false,
    canManageBookings: false,
    canManageTimeSlots: false,
    canManageWorkoutPlans: false,
    canViewReports: false,
    canViewAnalytics: false,
    canManageAI: false,
    canManageTokens: false,
    canManageExerciseLibrary: false,
    canAccessAITrainer: true,
    canAccessBookings: true,
    canAccessClasses: true,
    canAccessWorkoutPlans: true,
    canAccessMealPlans: true,
    canAccessGamification: true,
    canAccessTokenShop: true,
    canAccessMessages: true,
    canAccessCredits: true,
    canAccessSmartwatch: true,
    canAccessNotifications: true,
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data);
    } catch (error) {
      console.error('Erro ao carregar roles:', error);
      toast.error('Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingRole(null);
    setFormData({
      roleName: '', description: '',
      canManageUsers: false, canManageBookings: false, canManageTimeSlots: false,
      canManageWorkoutPlans: false, canViewReports: false, canViewAnalytics: false,
      canManageAI: false, canManageTokens: false, canManageExerciseLibrary: false, canAccessAITrainer: true,
      canAccessBookings: true, canAccessClasses: true, canAccessWorkoutPlans: true,
      canAccessMealPlans: true, canAccessGamification: true, canAccessTokenShop: true,
      canAccessMessages: true, canAccessCredits: true, canAccessSmartwatch: true,
      canAccessNotifications: true,
    });
    setShowModal(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setFormData({
      roleName: role.roleName,
      description: role.description || '',
      canManageUsers: role.canManageUsers,
      canManageBookings: role.canManageBookings,
      canManageTimeSlots: role.canManageTimeSlots,
      canManageWorkoutPlans: role.canManageWorkoutPlans,
      canViewReports: role.canViewReports,
      canViewAnalytics: role.canViewAnalytics,
      canManageAI: role.canManageAI,
      canManageTokens: role.canManageTokens,
      canManageExerciseLibrary: role.canManageExerciseLibrary ?? false,
      canAccessAITrainer: role.canAccessAITrainer ?? true,
      canAccessBookings: role.canAccessBookings ?? true,
      canAccessClasses: role.canAccessClasses ?? true,
      canAccessWorkoutPlans: role.canAccessWorkoutPlans ?? true,
      canAccessMealPlans: role.canAccessMealPlans ?? true,
      canAccessGamification: role.canAccessGamification ?? true,
      canAccessTokenShop: role.canAccessTokenShop ?? true,
      canAccessMessages: role.canAccessMessages ?? true,
      canAccessCredits: role.canAccessCredits ?? true,
      canAccessSmartwatch: role.canAccessSmartwatch ?? true,
      canAccessNotifications: role.canAccessNotifications ?? true,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.roleName.trim()) {
      toast.error('O nome do perfil é obrigatório');
      return;
    }
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, { id: editingRole.id, ...formData });
        toast.success('Perfil atualizado com sucesso!');
      } else {
        await api.post('/roles', formData);
        toast.success('Perfil criado com sucesso!');
      }
      setShowModal(false);
      loadRoles();
    } catch (error) {
      toast.error('Erro ao guardar perfil: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Eliminar o perfil "${role.roleName}"? Esta ação não pode ser revertida.`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success('Perfil eliminado!');
      loadRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao eliminar perfil');
    }
  };

  const selectAll = () => {
    const updated = { ...formData };
    ALL_PERMISSIONS.forEach(p => updated[p.key] = true);
    setFormData(updated);
  };

  const clearAll = () => {
    const updated = { ...formData };
    ALL_PERMISSIONS.forEach(p => updated[p.key] = false);
    setFormData(updated);
  };

  const countPermissions = (role) => ALL_PERMISSIONS.filter(p => role[p.key]).length;

  if (loading) {
    return (
      <DashboardLayout userType="admin">
        <div className="role-loading"><div className="spinner"></div><p>A carregar perfis...</p></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="admin">
      <div className="role-management">
        <div className="role-header">
          <div>
            <h1>🔐 Gestão de Perfis e Permissões</h1>
            <p>Crie perfis personalizados (Admin, Cliente, Personal Trainer, etc.) e defina as permissões de cada um.</p>
          </div>
          <button className="btn-create" onClick={openCreate}>+ Novo Perfil</button>
        </div>

        <div className="roles-grid">
          {roles.map(role => (
            <div key={role.id} className="role-card">
              <div className="role-card-header">
                <h3>{role.roleName}</h3>
                <span className="perm-count">{countPermissions(role)}/{ALL_PERMISSIONS.length} permissões</span>
              </div>
              {role.description && <p className="role-desc">{role.description}</p>}
              <div className="role-perms-sections">
                <div className="role-perms-section">
                  <h4 className="perms-section-title">🔧 Administração</h4>
                  <div className="role-perms">
                    {ADMIN_PERMISSIONS.map(p => (
                      <div key={p.key} className={`perm-item ${role[p.key] ? 'active' : 'inactive'}`}>
                        <span className="perm-icon">{role[p.key] ? '✅' : '❌'}</span>
                        <span>{p.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="role-perms-section">
                  <h4 className="perms-section-title">📱 Acesso a Funcionalidades</h4>
                  <div className="role-perms">
                    {FEATURE_PERMISSIONS.map(p => (
                      <div key={p.key} className={`perm-item ${role[p.key] ? 'active' : 'inactive'}`}>
                        <span className="perm-icon">{role[p.key] ? '✅' : '❌'}</span>
                        <span>{p.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="role-card-actions">
                <button className="btn-edit-role" onClick={() => openEdit(role)}>✏️ Editar</button>
                <button className="btn-delete-role" onClick={() => handleDelete(role)}>🗑️ Eliminar</button>
              </div>
            </div>
          ))}
        </div>

        {roles.length === 0 && (
          <div className="no-roles">
            <p>🔐 Nenhum perfil criado. Clique em "Novo Perfil" para começar.</p>
          </div>
        )}

        {showModal && (
          <div className="role-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="role-modal" onClick={e => e.stopPropagation()}>
              <div className="role-modal-header">
                <h2>{editingRole ? '✏️ Editar Perfil' : '➕ Novo Perfil'}</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
              </div>
              <div className="role-modal-body">
                <div className="role-form-group">
                  <label>Nome do Perfil *</label>
                  <input
                    type="text"
                    value={formData.roleName}
                    onChange={e => setFormData({ ...formData, roleName: e.target.value })}
                    placeholder="Ex: Personal Trainer, Rececionista, etc."
                  />
                </div>
                <div className="role-form-group">
                  <label>Descrição</label>
                  <textarea
                    rows="2"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Breve descrição do perfil..."
                  />
                </div>

                <div className="perms-section">
                  <div className="perms-header">
                    <h4>Permissões</h4>
                    <div>
                      <button className="btn-sm-perm" onClick={selectAll}>Selecionar Todas</button>
                      <button className="btn-sm-perm" onClick={clearAll}>Limpar Todas</button>
                    </div>
                  </div>

                  <h5 className="perms-group-title">🔧 Permissões de Administração</h5>
                  <div className="perms-grid">
                    {ADMIN_PERMISSIONS.map(p => (
                      <label key={p.key} className={`perm-checkbox ${formData[p.key] ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={formData[p.key]}
                          onChange={e => setFormData({ ...formData, [p.key]: e.target.checked })}
                        />
                        <div className="perm-cb-content">
                          <span className="perm-cb-icon">{p.icon}</span>
                          <div>
                            <strong>{p.label}</strong>
                            <small>{p.desc}</small>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <h5 className="perms-group-title">📱 Acesso a Funcionalidades do Cliente</h5>
                  <p className="perms-group-desc">Controle quais menus e funcionalidades ficam visíveis para os utilizadores com este perfil.</p>
                  <div className="perms-grid">
                    {FEATURE_PERMISSIONS.map(p => (
                      <label key={p.key} className={`perm-checkbox ${formData[p.key] ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={formData[p.key]}
                          onChange={e => setFormData({ ...formData, [p.key]: e.target.checked })}
                        />
                        <div className="perm-cb-content">
                          <span className="perm-cb-icon">{p.icon}</span>
                          <div>
                            <strong>{p.label}</strong>
                            <small>{p.desc}</small>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="role-modal-footer">
                <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
                <button className="btn-save" onClick={handleSave}>
                  {editingRole ? 'Guardar Alterações' : 'Criar Perfil'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RoleManagement;
